import { useEffect, useMemo, useRef } from 'react';

import { IRestaurantNode, useGetFavoriteRestaurantsQuery } from 'generated/graphql-gateway';
import { useAuthContext } from 'state/auth';

export const useGetFavoriteRestaurants = () => {
  const { isAuthenticated, user } = useAuthContext();
  // Token can exist before GetMe/session finish; the query stays skipped until then,
  // and Apollo reports loading=false for a skipped query.
  const waitingForUser = isAuthenticated && !user?.cognitoId;

  const favoriteStoreKey = useMemo(
    () =>
      (user?.details?.favoriteStores ?? [])
        .map(store => store?.storeId)
        .filter(Boolean)
        .join(','),
    [user?.details?.favoriteStores]
  );

  const { data, loading, error, refetch } = useGetFavoriteRestaurantsQuery({
    variables: {
      input: {
        first: 20,
      },
    },
    skip: !isAuthenticated || !user?.cognitoId,
    fetchPolicy: 'cache-and-network',
  });

  const previousFavoriteStoreKeyRef = useRef<string | null>(null);

  // The heart writes to `user.details.favoriteStores`, not this query. Refetch when that
  // list changes so restaurant nodes for newly saved stores stay available after Nearby
  // results move on (and so removed stores drop out of the cache).
  useEffect(() => {
    if (!isAuthenticated || !user?.cognitoId) {
      previousFavoriteStoreKeyRef.current = null;
      return;
    }

    if (previousFavoriteStoreKeyRef.current === null) {
      previousFavoriteStoreKeyRef.current = favoriteStoreKey;
      return;
    }

    if (previousFavoriteStoreKeyRef.current === favoriteStoreKey) {
      return;
    }

    previousFavoriteStoreKeyRef.current = favoriteStoreKey;
    refetch();
  }, [favoriteStoreKey, isAuthenticated, refetch, user?.cognitoId]);

  return {
    restaurants: (data?.favoriteRestaurants?.nodes as IRestaurantNode[]) || [],
    loading: loading || waitingForUser,
    error,
  };
};
